import { isPromise, call, ensureFunc } from ".";

/**
 * Type `SyncType<T>` - Type extracts type packed into the Promise
 *
 * @template T
 * @typedef {T extends Promise<infer Packed> ? Packed : T} SyncType
 */

const packToBuf = (buffer, field) => result => {
	// eslint-disable-next-line no-param-reassign
	buffer[field] = result;
	return buffer;
};

const resolveToBuffer = ([field, resolver]) => (args, buffer) => {
	try {
		const result = resolver(...args);
		const pack = packToBuf(buffer, field);
		return isPromise(result) ? result.then(pack) : pack(result);
	} catch (err) {
		return Promise.reject(err);
	}
};

const resolversWrapper = resolvers => (...args) => {
	const buffer = {};
	const promises = resolvers.map(call(args, buffer));
	return Promise.all(promises).then(() => buffer);
};

const prepareResolvers = ([field, resolver]) =>
	resolveToBuffer([field, ensureFunc(resolver, () => resolver)]);
/**
 * Creates new functions that returns Promise resolving to an object with
 * values returned by functions specified for correspondent fields of argument
 * object
 *
 * @example
 * const fn = propsResolver({
 * 	x: () => 1,
 *  y: () => 2,
 * });
 * fn().then(console.log); // prints: { x: 1, y: 2 }
 *
 * @type {
 	<
		A extends any[],
		M extends { [p in string]: (...args: any[]) => any }
	>(resolversMap: M) => (...args: A) => Promise<{ [k in keyof M]: SyncType<ReturnType<M[k]>> }>
 }
 */
const propsResolver = resolversMap =>
	resolversWrapper(
		Object.entries(resolversMap).map(prepareResolvers), // prepared resovlers
	);

export default propsResolver;
